/**
 * Rider Session earnings — "Bednbody Rider Rate — Final" (Luigi, 2026-08-23),
 * superseding RIDER-020/030 and CORE-07 §4.2/§4.3 as written in the vault
 * today. Leon's call: use the new numbers now; the vault pages themselves are
 * Luigi's to update on his side (never-invent-policy's boundary — this repo
 * does not hand-edit the imported product-spec).
 *
 * Pure, Deno-importable, every exported function unit-tested. The Edge Function
 * recomputes all of it server-side; the Balance screen only PREVIEWS
 * (money-math.md). Nothing here reads a clock, a database or an environment.
 *
 * ⚠ OQ-71 IS RESOLVED. As of `supabase/migrations/0118` (2026-08-23, Leon's
 * call), `run_payroll` reads the settled output of THIS module
 * (`rider_session_earnings`, written by the settlement pipeline in 0064/0065)
 * instead of the old `base_pay_rate` + snapshotted-mileage model. What this
 * module computes now drives both the Balance screen AND actual pay — they
 * are the same numbers by construction, not reconciled after the fact.
 *
 * ⚠ THE LONG-DISTANCE SHARE IS IMPLEMENTED — THIS NOTE USED TO DENY IT.
 *
 * It read: "The long-distance share pool does not exist in this system at all,
 * so there is no function for it here." That was wrong on both counts, and the
 * function it says is missing lives one file away:
 *
 *   · `longDistanceShares()` and `isLongDistanceEligible()` in
 *     `distance-surcharge.ts`, in THIS package, mirror §4.4. Since 0221 that is
 *     two role pools, half the surcharge each, with every share fixed BEFORE
 *     rank: therapists qualify at Silver/Gold/Diamond, riders only at
 *     Gold/Diamond, and an unqualified rider is paid ₱0 with his share KEPT by
 *     Bednbody — never handed to the therapist, which is what this note used
 *     to describe (RIDER-026: "the unpaid share is not transferred").
 *   · The pool is real and stored: `bookings.long_distance_surcharge`
 *     (0129/0131), divided at Service completion by
 *     `settle_booking_long_distance()` (latest 0221), banked in
 *     `booking_long_distance_shares` and paid by payroll (0133/0174).
 *
 * The TS side is the MIRROR, not the payer — the SQL is the truth, and
 * `supabase/tests/132-long-distance-shares.sql` is the other half of the same
 * claim. Change one, change both.
 */

import { round2 } from "./booking-amounts.ts";
import { toManila } from "./manila-time.ts";

/** Flat per-trip base fare, covering the first BASE_FARE_COVERS_KM. */
export const BASE_FARE_PHP = 40;

/** How much distance the base fare covers before the per-km rate starts. */
export const BASE_FARE_COVERS_KM = 5;

/** Marginal rate for every kilometre beyond BASE_FARE_COVERS_KM. */
export const ADDITIONAL_RATE_PER_KM_PHP = 3;

/**
 * The rider rate as one value, so a settlement can price with the rate a
 * superadmin saved in Settings (Luigi, 2026-09-11; `operating_settings`, 0213)
 * instead of the constants above. The constants stay the approved rate and the
 * default.
 */
export interface RiderRate {
  /** Flat per-trip fare. */
  baseFarePhp: number;
  /** How much distance the flat fare covers. */
  baseCoversKm: number;
  /** Rate for every kilometre beyond `baseCoversKm`. */
  perKmPhp: number;
}

export const DEFAULT_RIDER_RATE: RiderRate = {
  baseFarePhp: BASE_FARE_PHP,
  baseCoversKm: BASE_FARE_COVERS_KM,
  perKmPhp: ADDITIONAL_RATE_PER_KM_PHP,
};

function isValidRiderRate(rate: RiderRate): boolean {
  return [rate.baseFarePhp, rate.baseCoversKm, rate.perKmPhp].every(
    (n) => Number.isFinite(n) && n >= 0,
  );
}

/**
 * The saved rate, or null when any part is not a finite number of 0 or more.
 * Postgres `numeric` arrives over PostgREST as a string, so the caller passes
 * `Number(...)` and a garbled value reaches this function as NaN.
 *
 * ⚠ NULL IS NOT "PAY NOTHING" AND NOT "PAY THE DEFAULT". The settlement leaves
 * the Session unsettled and the nightly backfill tries again, because an earnings
 * row is permanent (0064 refuses updates) — writing one at a rate nobody saved
 * cannot be taken back.
 */
export function riderRateFromSettings(row: {
  rider_base_fare_php: number;
  rider_base_covers_km: number;
  rider_per_km_php: number;
}): RiderRate | null {
  const rate: RiderRate = {
    baseFarePhp: row.rider_base_fare_php,
    baseCoversKm: row.rider_base_covers_km,
    perKmPhp: row.rider_per_km_php,
  };
  return isValidRiderRate(rate) ? rate : null;
}

/** Distance is rounded to one decimal place BEFORE it is priced (unchanged). */
export const DISTANCE_PRECISION_KM = 0.1;

/** ₱5 for each completed Dispatch inside a peak window — Silver rank and up. */
export const PEAK_INCENTIVE_PHP = 5;

/**
 * ₱50/hr baseline, continuously prorated by exact extended minutes — NOT a
 * per-started-hour step function. 30 minutes over pays ₱25 (Luigi's own
 * worked example), not a full hour's ₱50.
 *
 * ⚠ No ceiling is stated in the new rate (the old rule's 3-hour cap, RIDER-030,
 * is dropped here rather than carried over silently). If a cap was intended to
 * survive the revision, that is a question for Luigi, not a default to assume.
 */
export const OVERTIME_RATE_PER_HOUR_PHP = 50;

/** RIDER-029 / Gold+Diamond tier: the guaranteed floor for an eligible Session. */
export const SESSION_GUARANTEE_PHP = 600;

export type RiderRank = "bronze" | "silver" | "gold" | "diamond";

export type RiderSessionOutcome =
  | "completed"
  | "late_start"
  | "early_end"
  | "incomplete"
  | "no_show";

/**
 * The base earning for one verified Dispatch: a flat ₱40 per-trip base fare
 * covering the first 5 km, plus ₱3/km for every kilometre beyond that.
 *
 * The base fare is PER TRIP, not per kilometre driven within it — a Dispatch
 * that never leaves the first 5 km still earns the full ₱40, the same as a
 * trip that starts at 0 km. There is no reading of "Base Fare, inclusive of
 * the first 5 km" that pays less than ₱40 for a real, completed Dispatch.
 *
 * Rounding order is the rule, not an implementation detail: the DISTANCE is
 * rounded to 0.1 km first and the result is priced, rather than pricing the raw
 * distance and rounding the peso figure. 10.44 km pays ₱40 + 5.4 × ₱3 = ₱56.20,
 * not ₱40 + 5.44 × ₱3 = ₱56.32. Reversing the two would quietly disagree with
 * every hand-check anyone does.
 *
 * The figures above are the approved rate, which is `rate`'s default. The
 * settlement passes the rate saved in Settings instead; the rounding order is
 * the same either way.
 *
 * ⚠ `km` must be the server-computed road distance over `trips.track`.
 * Client-reported mileage is never payable (SECURITY §5) — this function cannot
 * enforce that and the caller must.
 */
export function verifiedDispatchEarning(km: number, rate: RiderRate = DEFAULT_RIDER_RATE): number {
  if (!Number.isFinite(km) || km < 0) {
    throw new RangeError("verified distance must be a non-negative number of kilometres");
  }
  if (!isValidRiderRate(rate)) {
    throw new RangeError("rider rate must be finite numbers of 0 or more");
  }
  const roundedKm = Math.round(km / DISTANCE_PRECISION_KM) * DISTANCE_PRECISION_KM;
  const extraKm = Math.max(0, roundedKm - rate.baseCoversKm);
  return round2(rate.baseFarePhp + extraKm * rate.perKmPhp);
}

/**
 * RIDER-023 — is this instant inside a peak-incentive window?
 *
 *   Mon, Thu, Fri   7:00 PM – 11:00 PM
 *   Sat, Sun        all day
 *   Tue, Wed        never
 *
 * Manila wall clock, always. A rider working 10 PM Friday in a browser set to
 * UTC is still working Friday evening, and the incentive follows the operation's
 * clock rather than the device's.
 */
export function isPeakWindow(completedAtIso: string): boolean {
  const manila = toManila(completedAtIso, "completedAt");
  // Noon on the Manila calendar date, read back as a weekday. Using the raw
  // instant's weekday would put a 12:30 AM Saturday Dispatch on Friday.
  const weekday = new Date(`${manila.date}T12:00:00+08:00`).getUTCDay();

  switch (weekday) {
    case 0: // Sunday
    case 6: // Saturday
      return true;
    case 1: // Monday
    case 4: // Thursday
    case 5: // Friday
      // 7:00 PM inclusive, 11:00 PM exclusive — the window is four hours, and
      // an incentive that also paid at 11:00:00 PM would be four hours and an
      // instant, which is the kind of boundary nobody notices until payroll.
      return manila.hour >= 19 && manila.hour < 23;
    default: // Tuesday, Wednesday
      return false;
  }
}

/**
 * ₱5 per completed Dispatch whose completion falls in a peak window —
 * Silver rank and up only. Bronze gets no Peak Booster at all (Luigi's rank
 * table, 2026-08-23); the earlier rule did not gate this by rank.
 */
export function peakIncentive(completedAtIsos: readonly string[], rank: RiderRank): number {
  if (rank === "bronze") return 0;
  const eligible = completedAtIsos.filter(isPeakWindow).length;
  return round2(eligible * PEAK_INCENTIVE_PHP);
}

/**
 * ₱50/hr, continuously prorated by the EXACT extended minutes — Luigi's
 * 2026-08-23 "Overtime Pay" rate, superseding the old per-started-hour step
 * function (OQ-75's ₱50-per-started-hour reading no longer applies; that
 * question is moot under continuous proration). 30 minutes over pays ₱25,
 * matching the rate's own worked example — not a full hour's ₱50.
 *
 * ⚠ No cap is stated. The old rule's 3-hour ceiling is not carried over here —
 * an unbounded overrun now scales linearly with no stated maximum. If that is
 * not the intent, it is a question for Luigi, not a default this function
 * should assume back in.
 */
export function extensionFee(actualExtensionMinutes: number): number {
  if (!Number.isFinite(actualExtensionMinutes) || actualExtensionMinutes < 0) {
    throw new RangeError("extension minutes must be a non-negative number");
  }
  return round2((actualExtensionMinutes / 60) * OVERTIME_RATE_PER_HOUR_PHP);
}

/**
 * The four things the ₱600 guarantee is measured against — RIDER-027,
 * CORE-07 §4.5 step 3, the Rider Earnings Decision Table § Guarantee basis:
 *
 *   guarantee_earnings_total = cancellation_fees + dispatch_fees
 *                            + qualified_rider_surcharge_shares + peak_boosters
 *
 * "Exclude overtime and every other incentive from the guarantee threshold."
 */
export interface GuaranteeEarningsParts {
  dispatchFeesPhp: number;
  peakBoostersPhp: number;
  surchargeSharesPhp: number;
  cancellationFeesPhp: number;
}

/**
 * `guarantee_earnings_total`. Overtime is not a parameter on purpose: it is paid
 * on top of the guaranteed amount "separately when valid", never used to reach
 * it, so there is no way to pass it in.
 */
export function guaranteeEarningsTotal(parts: GuaranteeEarningsParts): number {
  const values = [
    parts.dispatchFeesPhp,
    parts.peakBoostersPhp,
    parts.surchargeSharesPhp,
    parts.cancellationFeesPhp,
  ];
  if (!values.every((n) => Number.isFinite(n) && n >= 0)) {
    throw new RangeError("guarantee earnings parts must be non-negative numbers");
  }
  return round2(values.reduce((total, n) => total + n, 0));
}

/**
 * RIDER-027/029 — the ₱600 Session guarantee adjustment:
 * `max(0, ₱600 − guarantee_earnings_total)` (CORE-07 §4.5 step 5).
 *
 * Gold and Diamond only, NEVER prorated: it tops a Session up to ₱600 or pays
 * nothing, and a Session whose eligible total is ₱620 receives no adjustment
 * rather than a negative one.
 *
 * ⚠ ONLY A COMPLETED SESSION QUALIFIES AUTOMATICALLY. This used to let an Early
 * End keep the guarantee, on the reading that RIDER-029's forfeit list does not
 * name it. The approved text settles it the other way: CORE-07 §4.5 step 2,
 * "Normal qualification requires a Completed eligible Rider Session", and the
 * Rider Earnings Decision Table gives Early End only "System may approve full
 * ₱600" — the Operations review of RIDER-028 (`rider_session_guarantee_reviews`,
 * paid by payroll as its own adjustment since 0126). Paying it here too would
 * pay an unreviewed Early End automatically.
 */
export function guaranteeAdjustment(
  guaranteeEarningsTotalPhp: number,
  rank: RiderRank,
  outcome: RiderSessionOutcome,
): number {
  if (!Number.isFinite(guaranteeEarningsTotalPhp) || guaranteeEarningsTotalPhp < 0) {
    throw new RangeError("guarantee earnings total must be a non-negative number");
  }
  const rankEligible = rank === "gold" || rank === "diamond";
  if (!rankEligible || outcome !== "completed") return 0;
  return round2(Math.max(0, SESSION_GUARANTEE_PHP - guaranteeEarningsTotalPhp));
}

/**
 * RIDER-028 — what an APPROVED emergency Early End review pays.
 *
 * CORE-07 §4.5 step 8 lets a verified medical/emergency Early End "receive the
 * full guarantee" only through authorized Operations approval. The guarantee is
 * defined once, in step 5, as `max(0, ₱600 − guarantee_earnings_total)` — so
 * "the full guarantee" is the top-up to ₱600, NOT ₱600 on top of what the
 * Session already earned. Gold and Diamond only (RIDER-025): a lower rank is
 * owed ₱0 whatever the reviewer decides.
 *
 * The Session's outcome is not a parameter: the review exists only for Sessions
 * `review_rider_session_guarantee` (0067) accepts, and the "no Late Start"
 * condition is part of what the reviewer is approving.
 */
export function emergencyGuaranteeTopUp(guaranteeEarningsTotalPhp: number, rank: RiderRank): number {
  if (!Number.isFinite(guaranteeEarningsTotalPhp) || guaranteeEarningsTotalPhp < 0) {
    throw new RangeError("guarantee earnings total must be a non-negative number");
  }
  if (rank !== "gold" && rank !== "diamond") return 0;
  return round2(Math.max(0, SESSION_GUARANTEE_PHP - guaranteeEarningsTotalPhp));
}

/** One completed Dispatch, as the earnings calculation needs it. */
export interface DispatchEarningInput {
  /** Server-computed road distance over `trips.track`. Never client-reported. */
  verifiedKm: number;
  /** When the Dispatch completed, ISO with offset. Decides the peak incentive. */
  completedAt: string;
}

/**
 * The two guarantee inputs that are NOT earned per Dispatch. Both are settled
 * elsewhere and paid by payroll on their own lines — the Long-Distance share
 * (0132/0134, paid by 0133) and the after-pickup cancellation share (0144, paid
 * by 0145) — so they COUNT toward the ₱600 but are never paid again here.
 */
export interface SessionShares {
  /** Σ this rider's Long-Distance Surcharge shares in the Session. Only a rank-qualified rider is ever paid one. */
  surchargeSharesPhp: number;
  /** Σ this rider's cancellation-fee shares in the Session. */
  cancellationFeesPhp: number;
}

export const NO_SESSION_SHARES: SessionShares = { surchargeSharesPhp: 0, cancellationFeesPhp: 0 };

export interface RiderSessionEarnings {
  /** Σ per-Dispatch base fare + additional-km rate — the Dispatch Fees. */
  basePhp: number;
  /** ₱5 × eligible completed Dispatches, Silver rank and up — the Peak Boosters. */
  peakIncentivePhp: number;
  /** ₱50/hr, prorated by exact extended minutes — Overtime. Outside the guarantee. */
  extensionFeePhp: number;
  /** The Session's surcharge shares, as counted toward the guarantee. Paid by payroll, not in `totalPhp`. */
  surchargeSharesPhp: number;
  /** The Session's cancellation shares, as counted toward the guarantee. Paid by payroll, not in `totalPhp`. */
  cancellationFeesPhp: number;
  /** `guarantee_earnings_total` — what ₱600 was compared with. */
  guaranteeBasisPhp: number;
  /** The top-up to ₱600, or 0. Gold/Diamond and a Completed Session only. */
  guaranteePhp: number;
  /**
   * What THIS row pays: Dispatch Fees + Peak Boosters + Overtime + the top-up.
   * The two shares are paid by payroll's own lines, so they are counted toward
   * the guarantee but never added here — adding them would pay them twice.
   */
  totalPhp: number;
  /** Distance actually priced, after 0.1 km rounding — shown, not recomputed. */
  totalKm: number;
  dispatchCount: number;
}

/**
 * The whole Session, composed from the parts above.
 *
 * ⚠ THE GUARANTEE BASIS IS THE RULE, AND IT USED TO BE WRONG BOTH WAYS. It was
 * measured against base + peak + OVERTIME, where RIDER-027 and CORE-07 §4.5 say
 * cancellation fees + Dispatch Fees + qualified surcharge shares + Peak Boosters.
 * Counting overtime under-paid the top-up; leaving out the two shares over-paid
 * it. Now overtime is paid on top of the guaranteed amount, so for an eligible
 * Session what the rider receives in all is
 *
 *   max(₱600, guarantee_earnings_total) + overtime
 *
 * which is the approved "if it is below ₱600 pay ₱600, otherwise pay the actual
 * total … overtime paid separately".
 */
export function riderSessionEarnings(
  dispatches: readonly DispatchEarningInput[],
  actualExtensionMinutes: number,
  rank: RiderRank,
  outcome: RiderSessionOutcome,
  rate: RiderRate = DEFAULT_RIDER_RATE,
  shares: SessionShares = NO_SESSION_SHARES,
): RiderSessionEarnings {
  const basePhp = round2(
    dispatches.reduce((total, d) => total + verifiedDispatchEarning(d.verifiedKm, rate), 0),
  );
  const peakIncentivePhp = peakIncentive(
    dispatches.map((d) => d.completedAt),
    rank,
  );
  const extensionFeePhp = extensionFee(actualExtensionMinutes);

  // Validates both shares as non-negative numbers before any of them is used.
  const guaranteeBasisPhp = guaranteeEarningsTotal({
    dispatchFeesPhp: basePhp,
    peakBoostersPhp: peakIncentivePhp,
    surchargeSharesPhp: shares.surchargeSharesPhp,
    cancellationFeesPhp: shares.cancellationFeesPhp,
  });
  const guaranteePhp = guaranteeAdjustment(guaranteeBasisPhp, rank, outcome);

  const totalKm = round2(
    dispatches.reduce(
      (km, d) => km + Math.round(d.verifiedKm / DISTANCE_PRECISION_KM) * DISTANCE_PRECISION_KM,
      0,
    ),
  );

  return {
    basePhp,
    peakIncentivePhp,
    extensionFeePhp,
    surchargeSharesPhp: round2(shares.surchargeSharesPhp),
    cancellationFeesPhp: round2(shares.cancellationFeesPhp),
    guaranteeBasisPhp,
    guaranteePhp,
    totalPhp: round2(basePhp + peakIncentivePhp + extensionFeePhp + guaranteePhp),
    totalKm,
    dispatchCount: dispatches.length,
  };
}
